/**
 * Mockup visual de la "credencial" del plan, usada en PlanSuperadorStep
 * (paso 4) y PagoStep (paso 5) del wizard del titular. Colores: bordó para
 * MS1 ("rojo"), verde militar para MS2 ("verde"). Logo Medicus como
 * watermark al pie con inner shadow sutil (replica el nodo 9:645 del
 * Figma "Credencial").
 */

interface Props {
  variant: 'rojo' | 'verde';
  /**
   * Etiqueta opcional arriba a la izquierda (ej. "Plan MS2"), como en el
   * diseño de Figma del mail. Si se omite, la card muestra solo el watermark
   * al pie — el comportamiento original del wizard queda intacto.
   */
  label?: string;
}

export function PlanMockupCard({ variant, label }: Props) {
  const bg = variant === 'rojo' ? '#5a1118' : '#364818';
  return (
    <div
      className="onb-card-tilt"
      style={{
        background: bg,
        color: 'white',
        borderTop: '8.75px solid rgba(255, 255, 255, 0.3)',
        borderRadius: 32,
        padding: 'clamp(24px, 3.2vw, 36px)',
        aspectRatio: '1.55 / 1',
        minHeight: 240,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: label ? 'space-between' : 'flex-end',
        boxShadow: '0 20px 50px -16px rgba(0,0,0,0.35)',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {label && (
        <div
          style={{
            fontFamily: 'Satoshi, sans-serif',
            fontSize: 'clamp(16px, 2.6vw, 20px)',
            fontWeight: 600,
            letterSpacing: '-0.3px',
            color: 'white',
          }}
        >
          {label}
        </div>
      )}
      <CredencialMedicusLogo />
    </div>
  );
}

function CredencialMedicusLogo() {
  return (
    <svg
      viewBox="0 0 96 20"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Medicus"
      style={{ width: '100%', display: 'block' }}
    >
      <defs>
        <filter
          id="credencial-medicus-inner-shadow"
          x="-5%"
          y="-5%"
          width="110%"
          height="120%"
          filterUnits="objectBoundingBox"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="bg" />
          <feBlend mode="normal" in="SourceGraphic" in2="bg" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.4" />
          <feGaussianBlur stdDeviation="0.2" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.12 0"
          />
          <feBlend in2="shape" />
        </filter>
      </defs>
      <path
        d="M90.3939 8.3154C85.6573 7.08388 84.7552 6.89916 84.7552 5.42805C84.7552 3.8249 86.2915 3.26133 87.6148 3.26133C89.5967 3.26133 91.2148 3.8541 91.344 6.15001H95.3645C95.3645 1.73939 91.7402 0 87.7984 0C85.362 0 82.8065 0.984539 81.5428 2.94925L81.5382 0.454519H77.3827V12.322C77.3827 15.16 76.8126 16.4687 73.8461 16.4687C70.4876 16.4687 70.3364 14.4109 70.3364 12.322V0.454519H66.1834L66.2011 4.8581C65.0055 1.73452 62.0262 0 58.5636 0C53.9649 0 50.8309 2.73399 49.7143 6.66007V0.454519H45.5593V6.65822C44.5014 2.4996 41.3229 0.454519 37.3494 0.454519H13.7413L9.94076 13.5799H9.88465L5.84851 0.454519H0V19.5426H3.89021V7.05754H3.94034L8.01018 19.5426H11.5748L15.5417 7.22683V19.5426H37.3494C41.9674 19.5426 44.6598 16.9068 45.5593 13.0094V19.5426H49.7143V13.3822C50.8249 17.2504 53.948 20 58.5636 20C62.3649 20 65.2338 17.9443 66.3725 14.5756C67.0606 18.2351 69.5711 20 73.8461 20C77.3413 20 79.6587 18.7502 80.771 16.2984C82.0445 18.8715 85.0097 20 88.1429 20C93.2759 20 96 17.3791 96 13.8778C96 9.54473 91.7671 8.6617 90.3939 8.3154ZM29.4839 16.0164H19.6928V11.3373H28.3781V8.07379H19.6928V3.98195H29.4839V16.0164ZM37.0582 16.0164H33.6399V3.98195H36.3189C40.4434 3.98195 41.7672 6.44564 41.7672 10.2944C41.7672 14.5213 39.4655 16.0164 37.0582 16.0164ZM66.1834 13.3763H62.62C62.0287 15.1964 60.6488 16.4687 58.5636 16.4687C54.8325 16.4687 53.4323 13.2656 53.4323 10.0793C53.4323 6.7354 54.8325 3.53095 58.5636 3.53095C60.5434 3.53095 62.28 4.86531 62.6527 6.65067H66.1834V13.3763ZM88.3034 16.7372C86.0843 16.7372 84.1235 15.7622 84.1235 13.222H81.507C81.5274 12.9193 81.5382 12.6472 81.5382 12.322L81.5523 8.60952C82.5998 10.0526 84.4982 10.7061 86.3737 11.2016C89.1486 11.9517 91.9798 12.298 91.9798 14.3579C91.9798 16.3071 89.7572 16.7372 88.3034 16.7372Z"
        fill="white"
        fillOpacity={0.24}
        filter="url(#credencial-medicus-inner-shadow)"
      />
    </svg>
  );
}
